/**
 * useAIReferences
 *
 * Orchestration hook for AI reference image management.
 * Combines data loading with mutation handlers.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getAIReferenceImages, getProductAssetPool,
  type AIRefSlot, type AssetPoolItem,
} from '@/lib/api/products';
import type { AssetPickerResult } from './AssetPickerModal';
import { useAIRefMutations } from './useAIRefMutations';

export function useAIReferences(productId: string) {
  const [slots, setSlots] = useState<AIRefSlot[]>([]);
  const [assets, setAssets] = useState<AssetPoolItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activePickerSlot, setActivePickerSlot] = useState<number | null>(null);
  const [saving, setSaving] = useState<number | null>(null);
  const closePicker = useCallback(() => setActivePickerSlot(null), []);
  const mutations = useAIRefMutations({ productId, setSlots, setSaving, closePicker });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [slotsRes, assetsRes] = await Promise.all([
        getAIReferenceImages(productId),
        getProductAssetPool(productId),
      ]);
      if (slotsRes.success) setSlots(slotsRes.slots);
      if (assetsRes.success) setAssets(assetsRes.assets);
    } catch (err) {
      setError('Fehler beim Laden der KI-Referenzen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePickerResult = (result: AssetPickerResult) => {
    if (activePickerSlot === null) return;
    const slot = activePickerSlot;
    if (result.type === 'pool' && result.assetId) {
      mutations.setReference(slot, result.assetId, result.role);
    } else if (result.type === 'upload' && result.file) {
      mutations.uploadAndSet(slot, result.file, result.role);
    } else if (result.type === 'generate' && result.generatedImageId) {
      mutations.transferAndSet(slot, result.generatedImageId, result.role);
    }
  };

  return {
    slots, assets, loading, error, activePickerSlot, saving,
    setActivePickerSlot, handlePickerResult, handleClearSlot: mutations.clearSlot,
  };
}
